"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowUpRight, BadgeCheck, Check, Lock, Settings, ShieldCheck } from "@/components/icons";
import SectionHeader from "@/components/ui/section-header";
import { HeroGlow, PricingCtaSection } from "@/components/ui";
import FeatureCard from "@/components/ui/feature-card";
import {
  AccentIconChip,
  AccentPill,
  accentTextStyle,
} from "@/components/sections/tools/_shared/parts";
import type { ToolAccent, ToolIcon } from "@/components/sections/tools/_shared/types";
import { cn } from "@/lib/utils";
import { HELP_URL } from "@/lib/constants";
import type { ExtensionConfig } from "./types";

const easeOut: [number, number, number, number] = [0.22, 1, 0.36, 1];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

interface HeroProps {
  config: ExtensionConfig;
}

export function ExtensionHero({ config }: HeroProps) {
  const prefersReducedMotion = useReducedMotion();
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  // The demo video is markup-autoplaying so it still runs with JS disabled, exactly like the
  // GIF it replaces. Reduced motion is therefore enforced here rather than through props:
  // flipping `autoPlay` after the element exists would not stop a video that already started,
  // and hydration does not reliably reconcile those attributes.
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;

    if (prefersReducedMotion) {
      video.loop = false;
      video.controls = true;
      video.pause();
      video.currentTime = 0;
    } else {
      video.loop = true;
      video.controls = false;
      // Autoplay can still be refused (e.g. data saver); nothing to recover from if so.
      void video.play().catch(() => {});
    }
  }, [prefersReducedMotion]);

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? false : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay, ease: easeOut },
  });

  return (
    /* overflow-y-clip, never x: the HeroGlow ribbon is 100vw wide and has to
       bleed past the shell's frame hairlines like every other hero on the site.
       Clipping x would cut the backdrop back to the 1280px content column. */
    <section className="border-border relative overflow-y-clip border-b pt-28 pb-16 sm:pt-32 sm:pb-20">
      <HeroGlow align="left" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div className="min-w-0">
          {config.featured && (
            <motion.div {...fadeUp(0)} className="mb-4">
              <span className="border-border bg-foreground/5 text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium">
                <BadgeCheck size={13} className="text-brand-green" aria-hidden="true" />
                {config.featured}
              </span>
            </motion.div>
          )}
          <motion.p {...fadeUp(0.03)} className="text-muted-foreground mb-3 text-sm font-medium">
            {config.eyebrow}
          </motion.p>
          <motion.h1
            {...fadeUp(0.05)}
            className="text-foreground text-4xl leading-[1.08] font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            {config.heading}
          </motion.h1>
          <motion.p
            {...fadeUp(0.12)}
            className="text-muted-foreground mt-5 max-w-xl text-lg leading-relaxed"
          >
            {config.tagline}
          </motion.p>

          <motion.div {...fadeUp(0.18)} className="mt-8 flex flex-wrap items-center gap-3">
            <span className="cta-glow-btn-wrap">
              <a
                href={config.storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-glow-btn-inner"
              >
                {config.storeCta}
                <ArrowUpRight size={16} aria-hidden="true" />
              </a>
            </span>
          </motion.div>

          <motion.ul
            {...fadeUp(0.24)}
            className="text-muted-foreground mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
          >
            {config.heroPerks.map((perk) => (
              <li key={perk} className="flex items-center gap-2">
                <Check size={14} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
                {perk}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: easeOut }}
          className="relative min-w-0"
        >
          <div
            className="pointer-events-none absolute -inset-8 opacity-60"
            style={{
              background: `radial-gradient(ellipse 60% 55% at 50% 50%, color-mix(in srgb, var(--brand-green) 8%, transparent), transparent 70%)`,
            }}
            aria-hidden="true"
          />
          <figure
            className="border-border relative overflow-hidden rounded-xl border bg-[--color-surface-1]"
            style={{
              boxShadow: `0 20px 60px -24px color-mix(in srgb, var(--brand-green) 20%, transparent)`,
            }}
          >
            <div className="border-border flex items-center gap-3 border-b bg-[--color-surface-2] px-4 py-2.5">
              <div className="flex gap-1.5" aria-hidden="true">
                <span className="bg-foreground/10 size-2.5 rounded-full" />
                <span className="bg-foreground/10 size-2.5 rounded-full" />
                <span className="bg-foreground/10 size-2.5 rounded-full" />
              </div>
              <div className="border-border bg-background/60 text-muted-foreground flex min-w-0 flex-1 items-center gap-1.5 rounded-md border px-3 py-1 text-xs">
                <Lock size={10} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
                <span className="truncate">{config.storeUrlLabel}</span>
              </div>
            </div>
            {config.heroVideo ? (
              /* Silent screen recording, so it behaves like the GIF it replaces: loops, no
                 controls, muted (required for autoplay). The effect above stops it and adds
                 controls when the visitor prefers reduced motion. */
              <video
                ref={heroVideoRef}
                src={config.heroVideo.src}
                width={config.heroVideo.width}
                height={config.heroVideo.height}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                aria-label={`The Proxidize proxy extension for ${config.browserLabel} in action`}
                className="h-auto w-full"
              />
            ) : (
              <Image
                src={config.heroImage}
                alt={`The Proxidize proxy extension for ${config.browserLabel} in action`}
                width={800}
                height={440}
                unoptimized
                priority
                className="h-auto w-full"
              />
            )}
          </figure>
        </motion.div>
      </div>
    </section>
  );
}

interface IntroProps {
  config: ExtensionConfig;
}

export function IntroBlock({ config }: IntroProps) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <section className="full-bleed band-wash bg-band py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5, ease: easeOut }}
          className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16"
        >
          <div className="lg:self-start">
            <SectionHeader eyebrow="Overview" heading="A modern take on proxy settings" />
          </div>
          <div className="space-y-5">
            {config.intro.map((paragraph, index) =>
              index === 0 ? (
                <p
                  key={paragraph}
                  className="text-foreground border-l-2 pl-6 text-xl leading-relaxed"
                  style={{ borderColor: "color-mix(in srgb, var(--brand-green) 40%, transparent)" }}
                >
                  {paragraph}
                </p>
              ) : (
                <p key={paragraph} className="text-muted-foreground text-lg leading-relaxed">
                  {paragraph}
                </p>
              )
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

interface FeatureGridProps {
  config: ExtensionConfig;
}

export function FeatureGrid({ config }: FeatureGridProps) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <section className="py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Features"
            heading="Everything you need to manage proxies"
            body={`Purpose-built for ${config.browserLabel} and packed with the essentials. No clutter, no complicated setup.`}
          />
        </div>
        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
        >
          {/* wash keeps the grid off flat white on the light theme. "blue" is
              the single-hue variant: on a grid this size the warm corner of the
              default wash reads as a second kind of card. Rotates corner every
              third tile. */}
          {config.features.map((feature) => (
            <FeatureCard
              key={feature.title}
              title={feature.title}
              description={feature.description}
              wash="blue"
              className="card-hover card-spotlight h-full"
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

interface ShowcaseProps {
  config: ExtensionConfig;
}

export function ShowcaseTabs({ config }: ShowcaseProps) {
  const prefersReducedMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const shots = config.screenshots.filter((shot) => shot.src);

  return (
    <section className="full-bleed band-wash bg-band py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="See it in action"
            heading="Take a tour of the extension"
            body="The screens you'll use every day, all from the toolbar: your proxy manager in the popup, bulk and CSV import, and quick settings for smart routing, WebRTC protection, and auto-reload on change."
          />
        </div>

        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5, ease: easeOut }}
          className="grid items-center gap-6 lg:grid-cols-2 lg:gap-12"
        >
          <div className="flex flex-col gap-3">
            {shots.map((shot, index) => {
              const isActive = index === active;
              return (
                <button
                  key={shot.label}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActive(index)}
                  className={cn(
                    "group relative cursor-pointer overflow-hidden rounded-xl border p-5 text-left transition-colors",
                    // The selected step reads as a lit card: a blue-tinted fill
                    // and border over the band, against plain cards for the rest.
                    isActive ? "bg-card card-wash-blue" : "border-border bg-card/50 hover:bg-card"
                  )}
                  style={
                    isActive
                      ? {
                          borderColor: "color-mix(in srgb, var(--brand-green) 32%, transparent)",
                          boxShadow:
                            "0 10px 30px -18px color-mix(in srgb, var(--brand-green) 45%, transparent)",
                        }
                      : undefined
                  }
                >
                  <span className="relative flex items-center gap-3">
                    <span
                      className="flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-bold transition-colors"
                      style={
                        isActive
                          ? {
                              backgroundColor:
                                "color-mix(in srgb, var(--brand-green) 14%, transparent)",
                              borderColor:
                                "color-mix(in srgb, var(--brand-green) 26%, transparent)",
                              color: "var(--brand-green)",
                            }
                          : {
                              backgroundColor:
                                "color-mix(in srgb, var(--foreground) 6%, transparent)",
                              borderColor: "color-mix(in srgb, var(--foreground) 10%, transparent)",
                              color: "var(--muted-foreground)",
                            }
                      }
                    >
                      {index + 1}
                    </span>
                    <span
                      className={cn(
                        "text-base font-semibold transition-colors",
                        isActive ? "text-foreground" : "text-muted-foreground"
                      )}
                    >
                      {shot.label}
                    </span>
                  </span>
                  <span className="text-muted-foreground mt-2 block pl-10 text-sm leading-relaxed">
                    {shot.caption}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="border-border relative order-first mx-auto aspect-4/3 w-full max-w-130 self-center overflow-hidden rounded-xl border bg-[--color-surface-1] lg:order-0">
            {shots.map((shot, index) => (
              <Image
                key={shot.label}
                src={shot.src as string}
                alt={shot.label}
                fill
                sizes="(min-width: 1024px) 520px, 100vw"
                className={cn(
                  "object-cover transition-opacity duration-500",
                  index === active ? "opacity-100" : "opacity-0"
                )}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

interface StepsProps {
  config: ExtensionConfig;
}

export function Steps({ config }: StepsProps) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <section className="py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="How it works"
            heading={`Up and running in ${config.browserLabel} in under a minute`}
          />
        </div>
        <motion.ol
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5, ease: easeOut }}
          className="relative grid gap-10 sm:grid-cols-3 sm:gap-8"
        >
          <div
            className="via-border absolute top-5 right-12 left-12 hidden h-px bg-linear-to-r from-transparent to-transparent sm:block"
            aria-hidden="true"
          />
          {config.steps.map((step, index) => (
            <li key={step.title} className="relative">
              <span
                className="relative z-10 mb-5 flex size-10 items-center justify-center rounded-full border text-sm font-bold"
                style={{
                  // Mixed into the page fill rather than laid over it: the chip
                  // has to stay opaque to mask the connector line behind it.
                  backgroundColor: "color-mix(in srgb, var(--brand-green) 12%, var(--background))",
                  borderColor: "color-mix(in srgb, var(--brand-green) 30%, transparent)",
                  color: "var(--brand-green)",
                }}
              >
                {index + 1}
              </span>
              <h3 className="text-foreground text-lg font-semibold">{step.title}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                {step.description}
              </p>
            </li>
          ))}
        </motion.ol>
      </div>
    </section>
  );
}

interface PermissionsProps {
  config: ExtensionConfig;
}

export function Permissions({ config }: PermissionsProps) {
  const prefersReducedMotion = useReducedMotion();
  /* Both icons are blue, like every other card icon on the tools pages. Only
     the pill shifts hue, which is where required-vs-optional is stated. */
  const groups: {
    title: string;
    chip: string;
    icon: ToolIcon;
    chipAccent: ToolAccent;
    items: { label: string; why: string }[];
  }[] = [
    {
      title: "Required permissions",
      chip: "Required",
      icon: ShieldCheck,
      chipAccent: "blue",
      items: config.permissions.required,
    },
    {
      title: "Optional permissions",
      chip: "Optional",
      icon: Settings,
      chipAccent: "orange",
      items: config.permissions.optional,
    },
  ];

  return (
    <section className="full-bleed band-wash bg-band py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Permissions & data"
            heading="Transparent about what it uses"
            body="The extension runs locally in your browser and asks only for what it needs to apply and verify proxies."
          />
        </div>
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5, ease: easeOut }}
          className="grid grid-cols-1 gap-6 md:grid-cols-2"
        >
          {groups.map(({ title, chip, icon: Icon, chipAccent, items }) => (
            <div
              key={title}
              className="border-border bg-card card-wash-blue relative overflow-hidden rounded-2xl border p-6 sm:p-8"
            >
              <div className="relative mb-6 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <AccentIconChip icon={Icon} size="sm" />
                  <h3 className="text-foreground text-lg font-semibold">{title}</h3>
                </div>
                <AccentPill label={chip} accent={chipAccent} />
              </div>
              <ul className="relative space-y-5">
                {items.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <Check
                      size={16}
                      className="mt-0.5 shrink-0"
                      style={accentTextStyle()}
                      aria-hidden="true"
                    />
                    <span>
                      <span className="text-foreground block text-sm font-medium">
                        {item.label}
                      </span>
                      <span className="text-muted-foreground mt-0.5 block text-sm leading-relaxed">
                        {item.why}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

interface CtaProps {
  config: ExtensionConfig;
}

/* The site-wide closing band. The action is this page's own — a browser store
   listing rather than a trial — so it comes in through `actions`; the wash,
   headline scale, perk row and footnote are the shared CTA's, which is what
   makes this read as the same closing section as every other page. */
export function ExtensionCta({ config }: CtaProps) {
  return (
    <PricingCtaSection
      id="ext-cta-heading"
      heading={`Add the proxy extension to ${config.browserLabel}`}
      body={
        <>
          Free to install, easy to use, and works with any proxy. Pair it with{" "}
          <Link
            href="/proxy-server/mobile-proxies"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            Proxidize mobile
          </Link>{" "}
          or{" "}
          <Link
            href="/proxy-server/residential-proxies"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            residential proxies
          </Link>{" "}
          for the best results.
        </>
      }
      perks={config.heroPerks.map((label) => ({ icon: Check, label }))}
      actions={
        <span className="cta-glow-btn-wrap">
          <a
            href={config.storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-glow-btn-inner"
          >
            {config.storeCta}
            <ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </span>
      }
      footnote={
        <>
          Need help getting set up? Browse our{" "}
          <a
            href={HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            setup guides
          </a>
          .
        </>
      }
    />
  );
}
